import { driver, type Config, type DriveStep, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export interface TourStep extends DriveStep {
  /**
   * Runs right before the step is entered so the page can put its target in the wanted state
   * (select a tab, expand a section). The target itself must already be in the DOM: steps are
   * skipped when their target is missing, and that check happens before `prepare` runs.
   */
  prepare?: () => void;
}

export interface TourOptions extends Omit<Config, 'steps'> {
  steps: TourStep[];
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function resolveElement(element: DriveStep['element']): Element | null {
  if (!element) return null;
  if (typeof element === 'function') return element();
  if (typeof element === 'string') return document.querySelector(element);
  return element;
}

/** A step without a target always shows; one whose target is missing or has no box (unmounted pane, v-if) is skipped. */
function isAvailable(step: DriveStep): boolean {
  if (!step.element) return true;
  const element = resolveElement(step.element);
  return !!element && element.getClientRects().length > 0;
}

function isFullyInViewport(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
}

/**
 * Polls `read` until it returns the same value twice in a row, so the caller can wait out an
 * animation of unknown length. Capped, so something that never settles cannot stall the tour.
 */
function settled(read: () => string, { maxWaitMs, requireChange = false }: SettleOptions): Promise<void> {
  return new Promise((resolve) => {
    const pollMs = 30;
    const minWaitMs = 120;
    const started = performance.now();
    let last = read();
    let changed = false;
    let idlePolls = 0;

    const poll = () => {
      const now = read();
      if (now !== last) {
        changed = true;
        idlePolls = 0;
      } else {
        idlePolls++;
      }
      last = now;

      const elapsed = performance.now() - started;
      // Motion that was expected but has not started yet gets a grace period rather than counting
      // as settled straight away
      const stillWaitingToStart = requireChange && !changed;
      const isSettled = stillWaitingToStart ? elapsed >= minWaitMs : idlePolls >= 2;
      if (isSettled || elapsed >= maxWaitMs) {
        resolve();
      } else {
        setTimeout(poll, pollMs);
      }
    };

    setTimeout(poll, pollMs);
  });
}

interface SettleOptions {
  maxWaitMs: number;
  /** Wait out a grace period for motion that has not started yet (a scroll, a CSS transition). */
  requireChange?: boolean;
}

const scrollPosition = () => `${window.scrollX},${window.scrollY}`;
const elementBox = (element: Element) => {
  const { top, left, width, height } = element.getBoundingClientRect();
  return `${top},${left},${width},${height}`;
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Fades the current step's popover out; it would otherwise sit on screen while the page scrolls away from it. */
async function fadeOutPopover(tour: Driver): Promise<void> {
  const wrapper: HTMLElement | undefined = tour.getState('popover')?.wrapper;
  if (!wrapper) return;

  if (prefersReducedMotion()) {
    wrapper.style.display = 'none';
    return;
  }

  const fadeMs = 120;
  // The fade-in keyframes keep filling forwards and animation declarations outrank inline
  // styles, so the opacity below only takes effect once the animation is gone
  wrapper.style.animation = 'none';
  wrapper.style.transition = `opacity ${fadeMs}ms ease`;
  wrapper.style.opacity = '0';
  await wait(fadeMs);
}

/**
 * Scrolls the target on screen before the highlight moves. driver.js can scroll on its own, but it
 * starts its stage animation at the same time and re-snaps the stage on every scroll event, which
 * makes the highlight jump around while the page is still moving.
 */
async function bringIntoView(element: Element): Promise<void> {
  const smooth = !prefersReducedMotion();
  element.scrollIntoView({
    behavior: smooth ? 'smooth' : 'auto',
    block: element.clientHeight > window.innerHeight ? 'start' : 'center',
    inline: 'nearest',
  });

  if (smooth) await settled(scrollPosition, { maxWaitMs: 1000, requireChange: true });
}

/**
 * driver.js only re-measures the highlight on window scroll and resize, so content that loads or
 * animates after a step opened (a pane growing, a card folding out above the target) leaves the
 * cutout on the wrong spot. Watching the target and its ancestors catches both cases.
 */
function observeLayout(element: Element, onChange: () => void): () => void {
  if (typeof ResizeObserver === 'undefined') return () => undefined;

  const observer = new ResizeObserver(onChange);
  for (let node: Element | null = element; node && node !== document.documentElement; node = node.parentElement) {
    observer.observe(node);
  }

  return () => observer.disconnect();
}

/**
 * driver.js with the behaviour a tour over a live page needs: steps whose target is not rendered
 * are skipped (progress and Back/Next/Finish follow the steps that actually show), the page scrolls
 * to the target before the highlight moves, and the highlight tracks layout changes while a step
 * is open.
 */
export function createTour(options: TourOptions): Driver {
  const { steps, ...config } = options;
  let stopObserving: () => void = () => undefined;
  let navigating = false;
  // Steps wait for the page (targets mounting, a scroll, a pane opening), so a tour can be
  // discarded mid-wait. Without this it would then open on whatever page came next.
  let disposed = false;

  // `prepare` rides along unused by driver.js; goTo reads it from `steps`
  const driveSteps: DriveStep[] = steps.map((step) => ({
    ...step,
    onHighlighted: (element, driveStep, opts) => {
      if (element) stopObserving = observeLayout(element, () => tour.refresh());
      step.onHighlighted?.(element, driveStep, opts);
    },
    onDeselected: (element, driveStep, opts) => {
      stopObserving();
      step.onDeselected?.(element, driveStep, opts);
    },
  }));

  const availableIndexes = () => driveSteps.flatMap((step, index) => (isAvailable(step) ? [index] : []));
  const availability = () => driveSteps.map((step) => (isAvailable(step) ? '1' : '0')).join('');

  // The steps this run will show, fixed once the tour starts so the progress count cannot change
  // under the user. Recomputing it per step would also be wrong: a target that has not mounted yet
  // is indistinguishable from one this property will never have.
  let plan: number[] = [];

  const remaining = (from: number, direction: 1 | -1) => {
    const position = plan.indexOf(from);
    const rest = direction === 1 ? plan.slice(position + 1) : plan.slice(0, Math.max(position, 0)).reverse();
    // A step whose target disappeared mid-tour (a card the user just edited away) is passed over
    return rest.find((index) => isAvailable(driveSteps[index]));
  };

  // driver.js ignores keyboard navigation while the highlight is still moving between steps but
  // not popover clicks, and a click mid-move leaves the previous target marked as active. Its stage
  // animation is a fixed 400ms.
  const stageAnimationMs = 400;
  let stageBusyUntil = 0;
  const isTransitioning = () => performance.now() < stageBusyUntil;

  async function goTo(requestedIndex: number, isStart = false): Promise<void> {
    if (navigating || disposed) return;
    navigating = true;

    try {
      if (isStart) {
        // The page is usually still mounting when the tour starts: its nav, cards and asides can
        // each arrive a tick later. Counting steps before then skips targets that do exist, which
        // reads as the tour not starting at all.
        await settled(availability, { maxWaitMs: 1000 });
        if (disposed) return;
        plan = availableIndexes();
      }

      // Only planned steps are navigated to; a stray index snaps to the next planned one
      const index = plan.includes(requestedIndex)
        ? requestedIndex
        : (plan.find((planned) => planned >= requestedIndex) ?? plan[0] ?? requestedIndex);

      const prepare = steps[index].prepare;
      if (prepare) prepare();

      const target = resolveElement(driveSteps[index].element);

      // `prepare` typically animates something open, which keeps moving the target (and everything
      // below it) for a few hundred ms. Highlighting or scrolling before that settles is what makes
      // the cutout chase the page instead of landing on it.
      if (prepare && target) {
        await settled(() => elementBox(target), { maxWaitMs: 1200, requireChange: true });
        if (disposed) return;
      }

      if (target && !isFullyInViewport(target)) {
        await fadeOutPopover(tour);
        await bringIntoView(target);
        if (disposed) return;
      }

      const position = plan.indexOf(index);
      const isLast = position === plan.length - 1;
      const step = driveSteps[index];
      step.popover = {
        ...step.popover,
        progressText: `${position + 1} of ${plan.length}`,
        disableButtons: position <= 0 ? ['previous'] : [],
        nextBtnText: isLast ? (step.popover?.doneBtnText ?? config.doneBtnText ?? 'Done') : config.nextBtnText,
      };

      stageBusyUntil = tour.getConfig().animate ? performance.now() + stageAnimationMs : 0;
      isStart ? tour.drive(index) : tour.moveTo(index);
    } finally {
      navigating = false;
    }
  }

  function moveNext(): void {
    if (isTransitioning()) return;
    const next = remaining(tour.getActiveIndex() ?? -1, 1);
    next === undefined ? tour.destroy() : void goTo(next);
  }

  function movePrevious(): void {
    if (isTransitioning()) return;
    const previous = remaining(tour.getActiveIndex() ?? 0, -1);
    if (previous !== undefined) void goTo(previous);
  }

  const tour = driver({
    ...config,
    steps: driveSteps,
    animate: config.animate ?? !prefersReducedMotion(),
    // Scrolling is done in `bringIntoView` before the highlight moves
    smoothScroll: false,
    onPopoverRender: (popover, opts) => {
      config.onPopoverRender?.(popover, opts);
      // driver.js focuses the close button right after this hook, so a keyboard user's Enter would
      // end the tour and every step would open with a ring on the ×. Focus the dialog itself instead;
      // Tab still reaches the buttons and the arrow keys still navigate.
      popover.wrapper.tabIndex = -1;
      queueMicrotask(() => popover.wrapper.focus({ preventScroll: true }));
    },
    onNextClick: moveNext,
    onPrevClick: movePrevious,
    onDestroyed: (element, step, opts) => {
      disposed = true;
      stopObserving();
      config.onDestroyed?.(element, step, opts);
    },
  });

  return {
    ...tour,
    drive: (index = 0) => void goTo(index, true),
    moveTo: (index) => void goTo(index),
    moveNext,
    movePrevious,
    destroy: () => {
      disposed = true;
      tour.destroy();
    },
  };
}
